<template>
    <div>
        <div
            v-for="comment in comments"
            :key="comment.id"
            class="d-flex mb-3"
            :class="{
                'justify-content-start mr-sm-3 mr-md-5': !isOwnComment(comment),
                'justify-content-end ml-sm-3 ml-md-5': isOwnComment(comment),
            }"
        >
            <div>
                <div class="small mb-1">
                    <span class="mr-2 comment-username">
                        <span>{{ comment.session.anonymous_username }} </span>
                        <span v-if="showLinkDetails" class="text-secondary"
                            >on share link
                            <a :href="shareLinkUrl" target="_blank"
                                >{{ comment.session.share_link.name }}
                                <span class="icon icon-100 icon-glyphicons-basic-square-new-window"></span
                            ></a>
                        </span>
                        <span
                            v-show="isOwnComment(comment) && editMode"
                            @click="openEditUsername()"
                            class="icon-btn icon icon-glyphicons-basic-square-edit"
                            role="button"
                            tabindex="0"
                        ></span>
                    </span>
                    <span class="text-secondary">{{ displayDate(comment.created) }}</span>
                </div>
                <div class="d-flex align-items-center">
                    <div
                        class="comment-bubble rounded"
                        :class="{
                            'comment-bubble-them': !isOwnComment(comment),
                            'comment-bubble-you': isOwnComment(comment),
                        }"
                    >
                        <div>
                            <span class="comment-bubble-text mr-1 align-middle">{{ comment.content }}</span>
                        </div>
                        <div
                            v-show="!showAdminTools && comment.is_resolved"
                            class="text-secondary small align-middle mt-1"
                        >
                            <span
                                class="icon icon-glyphicons-basic-circle-empty-check text-success align-middle mr-sm"
                            ></span>
                            <span class="align-middle">Resolved</span>
                        </div>
                    </div>
                    <resolve-button
                        v-if="showAdminTools"
                        @click="resolveComment(comment)"
                        :resolved="comment.is_resolved"
                        :disabled="caseIsReadOnly"
                    ></resolve-button>
                </div>
                <div
                    class="d-flex justify-content-end mt-1 mx-1"
                    v-if="isOwnComment(comment) && editMode && !comment.is_resolved"
                >
                    <span
                        @click="openEditComment(comment)"
                        class="icon-btn icon icon-100 icon-glyphicons-basic-square-edit align-middle"
                        role="button"
                        tabindex="0"
                    ></span>
                    <span
                        @click="openRemoveComment(comment)"
                        class="icon-btn icon icon-100 icon-glyphicons-basic-bin align-middle ml-1"
                        role="button"
                        tabindex="0"
                    ></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import ResolveButton from "@/components/ResolveButton"

export default {
    name: "NodeComments",
    props: {
        comments: {
            type: Array,
            required: true,
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        showAdminTools: {
            type: Boolean,
            required: false,
            default: false,
        },
        showLinkDetails: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        ResolveButton,
    },
    data() {
        return {
            currentComment: "",
            selectedComment: {
                content: "",
            },
        }
    },
    computed: {
        userId() {
            return this.$store.getters.user.id
        },
        sessionID() {
            return this.$route.params.sessionID
        },
        commentCount() {
            return this.comments.length
        },
        sessionCase() {
            return this.$store.getters.sessionCase
        },
        shareLinkUrl() {
            let caseId = this.$route.params.id || this.sessionCase.id
            return `/library/${caseId}/share`
        },
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
    },
    methods: {
        displayDate(dateStr) {
            return Utilities.formatDateTimeCondensed(dateStr)
        },
        // Returns true if the comment is tied to the authenticated user, or is tied to the current session.
        isOwnComment(comment) {
            let commentIsFromUser = comment.session.user_id == this.userId && Boolean(comment.session.user_id)
            return comment.session.id == this.sessionID || commentIsFromUser
        },
        openEditUsername() {
            this.$emit("open-edit-username")
        },
        openEditComment(comment) {
            this.$emit("open-edit-comment", comment)
        },
        openRemoveComment(comment) {
            this.$emit("open-remove-comment", comment)
        },
        resolveComment(comment) {
            // Mark as resolved.
            comment.is_resolved = !comment.is_resolved

            // Persist at server.
            http.put(`/api/comments/${comment.id}/resolve/`, {
                resolved: comment.is_resolved,
            })

            if (comment.is_resolved) {
                this.$set(comment, "recentlyResolved", true)
            }
        },
    },
    beforeDestroy() {
        // Kill all popovers.
        Utilities.destroyAllPopovers()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.comment-bubble {
    padding-top: 6px;
    padding-bottom: 6px;
    padding-left: 8px;
    padding-right: 8px;
    min-width: 250px;
    max-width: 100%;
}

.comment-bubble-them {
    background-color: $light;
}

.comment-bubble-you {
    background-color: #dee9ff;
}

.comment-bubble-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.comment-username {
    font-weight: 500;
}
</style>
